// Normalises a results endpoint response into a consistent paginated shape and
// resolves each result's XSL stylesheet.
//
// The paginated BE response is:
//   { results, totalCount, currentCount, currentOffset, xsls }
// where each result's `xsl` is an INTEGER index into the shared, de-duplicated
// `xsls` array (one entry per distinct stylesheet on the page) instead of the
// stylesheet repeated inline on every row. We resolve it back to the stylesheet
// string so downstream components keep reading `item.xsl` unchanged.
//
// Stays backward compatible with the legacy bare-array response so nothing
// breaks during the BE rollout.

const resolveXsls = (results, xsls) => {
  if (!Array.isArray(xsls)) return results;

  return results.map((result) =>
    typeof result?.xsl === "number"
      ? { ...result, xsl: xsls[result.xsl] ?? null }
      : result
  );
};

export const normalizeResultsResponse = (data) => {
  // Legacy: a bare array of results (pre-pagination BE). `serverPaginated:
  // false` tells the table the BE did NOT page this, so it should paginate
  // the full list client-side until the paginated BE rolls out.
  if (Array.isArray(data)) {
    return {
      results: data,
      totalCount: data.length,
      currentCount: data.length,
      currentOffset: 0,
      serverPaginated: false,
    };
  }

  // Results wrapper. `serverPaginated` hinges on a numeric `totalCount`: some
  // endpoints (lab/rad global) return the wrapper but ignore limit/offset and
  // send the full set with totalCount=null — those must be paged client-side.
  // A numeric totalCount means the BE actually paged the rows.
  if (data && (Array.isArray(data.results) || typeof data.totalCount === "number")) {
    const results = Array.isArray(data.results) ? data.results : [];
    const isServerPaginated = typeof data.totalCount === "number";

    return {
      results: resolveXsls(results, data.xsls),
      totalCount: isServerPaginated ? data.totalCount : results.length,
      currentCount: data.currentCount ?? results.length,
      currentOffset: data.currentOffset ?? 0,
      serverPaginated: isServerPaginated,
    };
  }

  return {
    results: [],
    totalCount: 0,
    currentCount: 0,
    currentOffset: 0,
    serverPaginated: false,
  };
};
